import { normalize } from "normalizr";
import { QueryOptions } from "odata-query";
import objectHash from "object-hash";

import { createAsyncActions } from "@ctra/utils";

import types from "./types";
import schemas, { NormalizedQnAList } from "./schema";
import { CreateTrackerFormValues, TrackerSource, TrackerSourceList } from "./typings";
import { ExtendedEventEntity } from "../events";

export type FetchQnAsFulfilledPayload = NormalizedQnAList & {
  id: TrackerSource["IdaQnAID"];
};

export type FetchQnAListFulfilledPayload = NormalizedQnAList & {
  hash: string;
};

/**
 * Fetch questions & answers from the oData endpoint
 * @type {AsyncActionCreator}
 */
const fetchAskIdaTracker = createAsyncActions(
  types.FETCH_ASK_IDA_TRACKER,
  (id: TrackerSource["IdaQnAID"]) => ({
    id
  }),
  (id: TrackerSource["IdaQnAID"], askIdaTrackerSource: TrackerSource): FetchQnAsFulfilledPayload => ({
    id,
    ...normalize(askIdaTrackerSource, schemas.askIdaTracker)
  }),
  (id, error) => ({ id, error })
);

/**
 * Fetch a list of Ask Ida trackers
 * @type {AsyncActionCreator}
 */
const fetchTrackerList = createAsyncActions(
  types.FETCH_ASK_IDA_TRACKER_LIST,
  (query: Partial<QueryOptions<unknown>>) => ({
    query,
    hash: objectHash(query)
  }),
  (hash: string, results: TrackerSourceList): FetchQnAListFulfilledPayload => ({
    hash,
    ...normalize(results, schemas.askIdaTrackerList)
  }),
  (hash, error) => ({ hash, error })
);

/**
 * Create a new Ask Ida tracker
 * @type {AsyncActionCreator}
 */
const createAskIdaTracker = createAsyncActions(
  types.CREATE_ASK_IDA_TRACKER,
  (event: ExtendedEventEntity & { trackerLabel: string }, formValues: CreateTrackerFormValues) => ({
    event,
    formValues
  }),
  (response: TrackerSource) => normalize(response, schemas.askIdaTracker),
  (error) => ({ error })
);

/**
 * Create a new Ask Ida tracker
 * @type {AsyncActionCreator}
 */
const deleteAskIdaTracker = createAsyncActions(
  types.DELETE_ASK_IDA_TRACKER,
  (id: TrackerSource["IdaQnAID"]) => ({
    id
  }),
  (id) => ({ id }),
  (id, error) => ({ id, error })
);

export default {
  createAskIdaTracker,
  fetchAskIdaTracker,
  deleteAskIdaTracker,
  fetchTrackerList
};
